import React from 'react';
import styled, { css } from 'styled-components';
import { MobileAppFlair } from 'types/graphql';
import { FLAIR_VIEWBOX, getFlairPath } from './get-flair-path';

type FlairWrapperProps = {
  flairType: MobileAppFlair;
  color: string;
  children: React.ReactNode;
};

/**
 * Wraps a mobile app grid section with a colored background and a matching flair
 * edge above and below the content.
 */
export function FlairWrapper({ flairType, color, children }: FlairWrapperProps): JSX.Element {
  const path = getFlairPath(flairType);

  if (flairType === MobileAppFlair.none || !path) {
    return <>{children}</>;
  }

  return (
    <Wrapper>
      <FlairEdge aria-hidden='true'>
        <svg viewBox={FLAIR_VIEWBOX} preserveAspectRatio='none'>
          <path d={path} fill={color} />
        </svg>
      </FlairEdge>
      <Content $color={color}>{children}</Content>
      <FlairEdge aria-hidden='true' $flipped $rotated={flairType === MobileAppFlair.angled}>
        <svg viewBox={FLAIR_VIEWBOX} preserveAspectRatio='none'>
          <path d={path} fill={color} />
        </svg>
      </FlairEdge>
    </Wrapper>
  );
}

const Wrapper = styled.div`
  width: 100%;
  margin-bottom: 40px;
`;

const Content = styled.div<{ $color: string }>`
  background: ${({ $color }) => $color};
`;

const FlairEdge = styled.div<{ $flipped?: boolean; $rotated?: boolean }>`
  height: 24px;
  line-height: 0;
  width: 100%;

  ${({ $flipped }) =>
    $flipped &&
    css`
      transform: scaleY(-1);
    `}
  ${({ $flipped, $rotated }) =>
    $flipped &&
    $rotated &&
    css`
      transform: scaleX(-1) scaleY(-1);
    `}

  svg {
    display: block;
    height: 100%;
    width: 100%;
  }
`;
